import type { ICalendarEvent, ICalendarCell } from '@/features/calendar/interfaces'
import { isToday, startOfDay } from 'date-fns'

import { useMemo } from 'react'
import { BookingBullet } from '@/features/calendar/components/month-view/booking-bullet'

import { MonthTrainingBadge } from '@/features/calendar/components/month-view/month-event-badge'
import { getMonthCellTrainings } from '@/features/calendar/helpers'

import { cn } from '@/lib/utils'

interface IProps {
  cell: ICalendarCell
  trainings: ICalendarEvent[]
  trainingPositions: Record<string, number>
}

const MAX_VISIBLE_TRAININGS = 3

export function DayCell({ cell, trainingPositions, trainings }: IProps) {
  const { day, currentMonth, date } = cell

  const cellTrainings = useMemo(() => getMonthCellTrainings(date, trainings, trainingPositions), [date, trainings, trainingPositions])
  const isSunday = date.getDay() === 0

  return (
    <div className={cn('flex flex-col gap-1 border-l border-t py-1.5 lg:py-2', isSunday && 'border-l-0')}>
      <span
        className={cn(
          'h-6 px-1 text-xs font-semibold lg:px-2',
          !currentMonth && 'opacity-20',
          isToday(date) && 'flex w-6 translate-x-1 items-center justify-center rounded-full bg-primary px-0 font-bold text-white',
        )}
      >
        {day}
      </span>

      <div className={cn('flex h-6 gap-1 px-2 lg:h-[94px] lg:flex-col lg:gap-2 lg:px-0', !currentMonth && 'opacity-50')}>
        {[0, 1, 2].map((position) => {
          const training = cellTrainings.find((e: any) => e.position === position)
          const trainingKey = training ? `training-${training.id}-${position}` : `empty-${position}`

          return (
            <div key={trainingKey} className="lg:flex-1">
              {training && (
                <>
                  <BookingBullet className="lg:hidden" color={training.color} />
                  <MonthTrainingBadge className="hidden lg:flex" cellDate={startOfDay(date)} training={training} />
                </>
              )}
            </div>
          )
        })}
      </div>

      {cellTrainings.length > MAX_VISIBLE_TRAININGS && (
        <p className={cn('h-4.5 px-1.5 text-xs font-semibold text-t-quaternary', !currentMonth && 'opacity-50')}>
          <span className="sm:hidden">
            +
            {cellTrainings.length - MAX_VISIBLE_TRAININGS}
          </span>
          <span className="hidden sm:inline">
            {' '}
            {cellTrainings.length - MAX_VISIBLE_TRAININGS}
            {' '}
            more...
          </span>
        </p>
      )}
    </div>
  )
}
